import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  RefreshCw, CheckCircle2, AlertTriangle, Clock, Copy, Activity,
  ChevronDown, ChevronRight, Trash2, CalendarClock,
} from "lucide-react";
import { toast } from "sonner";

/** Halaman monitor sinkronisasi Google Calendar per armada.
 *  v84.47 — Mode satu-arah (App → GCal). Menampilkan log push per armada.
 *  Menampilkan: nama tech, calendar_id, success/error counts, last error dgn
 *  http_status + operation, mismatched calendar detection, recent errors log.
 */
export default function GoogleSync() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hours, setHours] = useState(24);
  const [expanded, setExpanded] = useState({});    // tech_id → bool
  const [repushing, setRepushing] = useState(false);  // v84.55 — Repush Links state

  // v85.44 — Push Ulang Sync by date
  const [pushDate, setPushDate] = useState(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  });
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);  // { date, summary, details }

  async function load() {
    setLoading(true);
    try {
      const r = await api.googleSyncStatus(hours);
      setData(r);
    } catch (e) {
      toast.error("Gagal muat status: " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hours]);

  async function runCleanup() {
    try {
      const r = await api.gcalSyncEventsCleanup(30);
      toast.success(`Cleanup selesai: ${r.deleted ?? 0} dokumen dihapus (>30 hari).`);
      await load();
    } catch (e) {
      toast.error("Cleanup gagal: " + (e?.response?.data?.detail || e.message));
    }
  }

  // v84.55 — Manual push maps + billing link ke event GCal yg desc-nya belum lengkap
  async function runRepushLinks() {
    setRepushing(true);
    try {
      const r = await api.gcalRepushMissingLinks({ dry_run: false, limit: 500 });
      const s = r.stats || {};
      toast.success(
        `Repush selesai — scan=${s.total_scanned || 0}, ok=${s.already_ok || 0}, ` +
        `missing_maps=${s.missing_maps || 0}, missing_billing=${s.missing_billing || 0}, ` +
        `flag_reset=${s.flag_reset || 0}, repushed=${s.repushed_ok || 0}, fail=${s.repush_fail || 0}`
      );
      await load();
    } catch (e) {
      toast.error("Repush gagal: " + (e?.response?.data?.detail || e.message));
    } finally {
      setRepushing(false);
    }
  }

  // v85.44 — Push ulang semua WO di tanggal tertentu ke Google Calendar (upsert).
  // Tanpa filter status, jadi WO CANCELLED/DONE/SCHEDULED semuanya ikut.
  async function runPushSyncByDate() {
    if (!pushDate) { toast.error("Pilih tanggal dulu"); return; }
    setPushing(true);
    setPushResult(null);
    try {
      const r = await api.pushSyncByDate(pushDate);
      setPushResult(r);
      const s = r.summary || {};
      toast.success(
        `Push ${pushDate} selesai — ${s.pushed || 0} berhasil ` +
        `(update=${s.updated || 0}, create=${s.created || 0}), ` +
        `skip=${s.skipped || 0}, gagal=${s.failed || 0}`,
        { duration: 6000 },
      );
      await load();
    } catch (e) {
      toast.error("Push gagal: " + (e?.response?.data?.detail || e.message));
    } finally {
      setPushing(false);
    }
  }

  function copyCal(cal) {
    navigator.clipboard.writeText(cal);
    toast.success(`Calendar ID disalin: ${cal}`);
  }

  const totals = data?.totals || { success: 0, error: 0, skipped: 0 };
  const teams = data?.teams || [];

  return (
    <div className="space-y-4" data-testid="google-sync-page">
      {/* v84.47 — Banner one-way sync */}
      <div className="border border-amber-500/30 bg-amber-500/10 rounded-sm p-3 flex items-start gap-2" data-testid="one-way-banner">
        <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="text-[12px] text-amber-900 dark:text-amber-100">
          <div className="font-semibold">Mode Satu-Arah Aktif · App → Google Calendar</div>
          <div className="text-[11px] opacity-90 mt-0.5">
            Sejak v84.47, sinkronisasi hanya berjalan satu arah: perubahan WO di aplikasi otomatis di-push ke Google Calendar (buat, edit, pindah, hapus, warna billing).
            Pull-sync dari Google ke aplikasi <b>dinonaktifkan</b> supaya WO lokal tidak ter-overwrite oleh edit manual di GCal.
            Halaman ini tetap menampilkan riwayat sync (log push) supaya Anda bisa monitor error dari sisi push.
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Google Sync Status</h1>
          <p className="text-sm text-zinc-500 mt-1">Monitor push App → Google Calendar per armada · window {hours} jam terakhir</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={hours} onChange={e=>setHours(parseInt(e.target.value))}
            className="rounded-sm border border-zinc-200 h-9 px-2 text-sm"
            data-testid="window-select">
            <option value={1}>1 jam</option>
            <option value={6}>6 jam</option>
            <option value={24}>24 jam</option>
            <option value={72}>3 hari</option>
            <option value={168}>7 hari</option>
            <option value={720}>30 hari (max)</option>
          </select>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}
            className="h-9 rounded-sm gap-1.5" data-testid="refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${loading?"animate-spin":""}`} /> Refresh
          </Button>

          {/* v84.55 — Manual repush maps + billing link */}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" disabled={repushing}
                className="h-9 rounded-sm gap-1.5 text-emerald-500 border-emerald-500/40"
                data-testid="repush-links-btn"
                title="Push ulang Maps + Billing link ke event GCal yg desc-nya belum lengkap">
                <Activity className={`w-3.5 h-3.5 ${repushing?"animate-pulse":""}`} />
                {repushing ? "…" : "Repush Links"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="repush-links-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Repush Maps + Billing Link</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-2 text-sm text-zinc-600">
                    <p>Scan semua WO (mulai hari ini ke depan) yang punya event GCal.</p>
                    <p>Untuk yang desc-nya <strong>missing</strong> baris <code className="bg-zinc-100 px-1 rounded">Maps:</code> atau marker <code className="bg-zinc-100 px-1 rounded">💰 Field Billing</code>:</p>
                    <ul className="list-disc ml-5 text-[13px] space-y-1">
                      <li>Reset flag <code>billing_link_sent=False</code> kalau marker billing hilang</li>
                      <li>Push ulang event via <code>sync_wo_to_google</code> — desc di-rebuild dari scratch</li>
                    </ul>
                    <p className="text-emerald-600 font-medium">Aman: hanya menambah/melengkapi, tidak menghapus data.</p>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="repush-links-cancel">Batal</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={runRepushLinks}
                  data-testid="repush-links-confirm">
                  Jalankan Repush
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* v85.44 — Push Ulang Sync by date (semua status) */}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" disabled={pushing}
                className="h-9 rounded-sm gap-1.5 text-[#2962FF] border-[#2962FF]/40"
                data-testid="push-sync-btn"
                title="Push ulang semua WO di tanggal tertentu ke Google Calendar (upsert, semua status)">
                <CalendarClock className={`w-3.5 h-3.5 ${pushing?"animate-pulse":""}`} />
                {pushing ? "Push…" : "Push Ulang Sync"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="push-sync-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Push Ulang Sync ke Google Calendar</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-3 text-sm text-zinc-600">
                    <p>Pilih tanggal WO yang mau di-sync ulang. Sistem akan memproses <strong>semua WO</strong> pada tanggal tsb (SCHEDULED, DONE, CANCELLED — tanpa filter status).</p>
                    <ul className="list-disc ml-5 text-[13px] space-y-1">
                      <li>Kalau event sudah ada di Google (lewat <code className="bg-zinc-100 px-1 rounded">google_event_id</code> atau Order-ID marker) → <strong>update</strong> judul, waktu, lokasi, deskripsi.</li>
                      <li>Kalau belum ada → <strong>buat event baru</strong> di kalender teknisi yg ditugaskan.</li>
                      <li>WO tanpa teknisi atau teknisi tanpa kalender ter-link → di-skip.</li>
                    </ul>
                    <div className="flex items-center gap-2 pt-2">
                      <label className="text-[12px] font-semibold text-zinc-700">Tanggal:</label>
                      <input
                        type="date"
                        value={pushDate}
                        onChange={e => setPushDate(e.target.value)}
                        className="h-9 px-2 rounded-sm border border-zinc-300 text-[13px] font-mono"
                        data-testid="push-sync-date-input"
                      />
                    </div>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="push-sync-cancel">Batal</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-[#2962FF] hover:bg-[#1E4FCC]"
                  onClick={runPushSyncByDate}
                  data-testid="push-sync-confirm">
                  Jalankan Push
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm"
                className="h-9 rounded-sm gap-1.5 text-zinc-600 border-zinc-300"
                data-testid="cleanup-btn"
                title="Hapus log lama >30 hari di gcal_sync_events">
                <Trash2 className="w-3.5 h-3.5" />
                Cleanup
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="cleanup-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Hapus log lama gcal_sync_events</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-2 text-sm text-zinc-600">
                    <p>Hapus semua dokumen di koleksi <code className="bg-zinc-100 px-1 rounded">gcal_sync_events</code> yang lebih tua dari <strong>30 hari</strong>.</p>
                    <p>Aksi ini permanent — tidak bisa di-undo. Data monitor akan tetap tersedia untuk 30 hari terakhir.</p>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="cleanup-cancel">Batal</AlertDialogCancel>
                <AlertDialogAction
                  onClick={runCleanup}
                  data-testid="cleanup-confirm">
                  Hapus Log Lama
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* v85.44 — Push Sync result panel */}
      {pushResult && (
        <div className="border border-[#2962FF]/30 bg-[#2962FF]/5 rounded-sm p-4" data-testid="push-sync-result">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-[#2962FF]" />
              <div className="font-semibold text-sm">
                Hasil Push Ulang Sync — <span className="font-mono">{pushResult.date}</span>
              </div>
            </div>
            <button
              onClick={() => setPushResult(null)}
              className="text-[11px] text-zinc-500 hover:text-zinc-800"
              data-testid="push-sync-result-close">
              Tutup ✕
            </button>
          </div>
          <div className="grid grid-cols-5 gap-2 text-center">
            <SummaryStat label="Total WO" value={pushResult.summary?.total || 0} color="zinc" testId="push-stat-total" />
            <SummaryStat label="Berhasil" value={pushResult.summary?.pushed || 0} color="emerald" testId="push-stat-pushed" />
            <SummaryStat label="Update" value={pushResult.summary?.updated || 0} color="blue" testId="push-stat-updated" />
            <SummaryStat label="Create Baru" value={pushResult.summary?.created || 0} color="indigo" testId="push-stat-created" />
            <SummaryStat label="Skip/Gagal" value={(pushResult.summary?.skipped || 0) + (pushResult.summary?.failed || 0)} color="amber" testId="push-stat-skipped" />
          </div>
          {Array.isArray(pushResult.details) && pushResult.details.length > 0 && (
            <details className="mt-3" data-testid="push-sync-detail-wrap">
              <summary className="text-[11px] text-zinc-600 cursor-pointer hover:text-zinc-900">
                Detail per WO ({pushResult.details.length} baris, max 100)
              </summary>
              <div className="mt-2 max-h-72 overflow-y-auto border border-zinc-200 rounded-sm bg-white">
                <table className="w-full text-[11px]">
                  <thead className="bg-zinc-50 sticky top-0">
                    <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500">
                      <th className="px-2 py-1.5">WO ID</th>
                      <th className="px-2 py-1.5">Aksi / Alasan</th>
                      <th className="px-2 py-1.5">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pushResult.details.map((d, i) => (
                      <tr key={i} className="border-t border-zinc-100 hover:bg-zinc-50">
                        <td className="px-2 py-1 font-mono text-[10px]">{(d.wo || "-").toString().slice(0, 12)}</td>
                        <td className="px-2 py-1">
                          {d.action ? (
                            <span className="text-emerald-700 font-semibold">{d.action}</span>
                          ) : (
                            <span className="text-amber-700">{d.reason || "—"}</span>
                          )}
                          {d.error && <span className="text-zinc-500 ml-1">· {d.error}</span>}
                        </td>
                        <td className="px-2 py-1 text-zinc-500 font-mono text-[10px]">{d.status || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>
      )}

      {/* Summary KPI */}
      <div className="grid grid-cols-3 gap-3">
        <div className="border border-emerald-200 bg-emerald-50 rounded-sm p-4">
          <div className="flex items-center gap-2 text-emerald-700 text-[11px] uppercase tracking-wider font-semibold">
            <CheckCircle2 className="w-3.5 h-3.5" /> Sukses
          </div>
          <div className="text-3xl font-bold text-emerald-800 mt-1" data-testid="total-success">{totals.success}</div>
        </div>
        <div className="border border-red-200 bg-red-50 rounded-sm p-4">
          <div className="flex items-center gap-2 text-red-700 text-[11px] uppercase tracking-wider font-semibold">
            <AlertTriangle className="w-3.5 h-3.5" /> Error
          </div>
          <div className="text-3xl font-bold text-red-800 mt-1" data-testid="total-error">{totals.error}</div>
        </div>
        <div className="border border-zinc-200 bg-zinc-50 rounded-sm p-4">
          <div className="flex items-center gap-2 text-zinc-600 text-[11px] uppercase tracking-wider font-semibold">
            <Clock className="w-3.5 h-3.5" /> Skipped
          </div>
          <div className="text-3xl font-bold text-zinc-700 mt-1" data-testid="total-skipped">{totals.skipped}</div>
        </div>
      </div>

      {/* Table per armada */}
      <div className="border border-zinc-200 rounded-sm bg-white overflow-hidden" data-testid="sync-table-wrapper">
        <Table>
          <TableHeader className="bg-zinc-50">
            <TableRow>
              <TableHead className="w-8"></TableHead>
              <TableHead>Armada</TableHead>
              <TableHead>Calendar ID (email)</TableHead>
              <TableHead className="text-center w-20">✓ Sukses</TableHead>
              <TableHead className="text-center w-20">✗ Error</TableHead>
              <TableHead>Sukses Terakhir</TableHead>
              <TableHead>Error Terakhir</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {teams.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-zinc-400 py-6">
                {loading ? "Memuat…" : "Belum ada teknisi aktif"}
              </TableCell></TableRow>
            )}
            {teams.map(t => {
              const isOpen = !!expanded[t.tech_id];
              const status = t.error_count > 0 ? "error" :
                             t.success_count > 0 ? "ok" :
                             t.linked ? "idle" : "unlinked";
              return (
                <React.Fragment key={t.tech_id}>
                  <TableRow
                    className={`cursor-pointer hover:bg-zinc-50 ${status==="error"?"bg-red-50/30":""}`}
                    onClick={()=>setExpanded(s=>({...s, [t.tech_id]: !isOpen}))}
                    data-testid={`sync-row-${t.tech_id}`}>
                    <TableCell>
                      {isOpen ? <ChevronDown className="w-4 h-4"/> : <ChevronRight className="w-4 h-4"/>}
                    </TableCell>
                    <TableCell className="font-semibold">
                      <div className="flex items-center gap-2">
                        <StatusDot status={status}/>
                        {t.tech_name}
                      </div>
                    </TableCell>
                    <TableCell>
                      {t.calendar_id ? (
                        <div className="flex items-center gap-1.5 group">
                          <code className="text-[11px] bg-zinc-100 px-1.5 py-0.5 rounded" data-testid={`cal-id-${t.tech_id}`}>{t.calendar_id}</code>
                          <button onClick={(e)=>{e.stopPropagation(); copyCal(t.calendar_id);}}
                            className="opacity-0 group-hover:opacity-100 hover:text-[#E65100]" title="Salin">
                            <Copy className="w-3 h-3"/>
                          </button>
                          {t.mismatched_cal_ids?.length > 0 && (
                            <Badge className="ml-1 bg-amber-100 text-amber-700 border-amber-300 text-[9px]" title={`Terdeteksi cal berbeda di log: ${t.mismatched_cal_ids.join(", ")}`}>
                              MISMATCHED ID
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-[11px] text-zinc-400 italic">— belum di-link —</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={t.success_count > 0 ? "text-emerald-700 font-mono font-bold" : "text-zinc-400 font-mono"}>{t.success_count}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={t.error_count > 0 ? "text-red-700 font-mono font-bold" : "text-zinc-400 font-mono"}>{t.error_count}</span>
                    </TableCell>
                    <TableCell className="text-[11px] text-zinc-600">
                      {t.last_success_at ? fmtDate(t.last_success_at) : <span className="text-zinc-400">—</span>}
                    </TableCell>
                    <TableCell className="text-[11px]">
                      {t.last_error ? (
                        <div className="space-y-0.5">
                          <div className="text-red-700 font-semibold">
                            {t.last_error_status ? `${t.last_error_status} ` : ""}
                            {t.last_error_operation}
                          </div>
                          <div className="text-zinc-600 truncate max-w-xs" title={t.last_error}>{t.last_error}</div>
                          <div className="text-[9px] text-zinc-400">{fmtDate(t.last_error_at)}</div>
                        </div>
                      ) : <span className="text-zinc-400">—</span>}
                    </TableCell>
                  </TableRow>
                  {isOpen && (
                    <TableRow className="bg-zinc-50/50">
                      <TableCell colSpan={7} className="p-3">
                        <ErrorLogPanel errors={t.recent_errors || []} techName={t.tech_name}/>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {data?.generated_at && (
        <div className="text-[10px] text-zinc-400 text-right">
          Data dibuat: {fmtDate(data.generated_at)} · Log auto-cleanup setelah 30 hari
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────
// ────────────────────────────────────────────────────────
function SummaryStat({ label, value, color = "zinc", testId }) {
  const pal = {
    zinc:    { border: "border-zinc-200",    bg: "bg-zinc-50",    text: "text-zinc-700" },
    emerald: { border: "border-emerald-200", bg: "bg-emerald-50", text: "text-emerald-800" },
    blue:    { border: "border-blue-200",    bg: "bg-blue-50",    text: "text-blue-800" },
    indigo:  { border: "border-indigo-200",  bg: "bg-indigo-50",  text: "text-indigo-800" },
    amber:   { border: "border-amber-200",   bg: "bg-amber-50",   text: "text-amber-800" },
  }[color] || { border: "border-zinc-200", bg: "bg-zinc-50", text: "text-zinc-700" };
  return (
    <div className={`border ${pal.border} ${pal.bg} rounded-sm px-2 py-1.5`} data-testid={testId}>
      <div className="text-[9px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</div>
      <div className={`text-xl font-bold font-mono ${pal.text} mt-0.5`}>{value}</div>
    </div>
  );
}

function StatusDot({ status }) {
  const cls = status==="ok" ? "bg-emerald-500"
            : status==="error" ? "bg-red-500 animate-pulse"
            : status==="idle" ? "bg-zinc-300"
            : "bg-amber-300";
  return <span className={`inline-block w-2 h-2 rounded-full ${cls}`} />;
}

function ErrorLogPanel({ errors, techName }) {
  if (errors.length === 0) return (
    <div className="text-[11px] text-zinc-500 text-center py-3">
      ✓ Tidak ada error dalam window ini untuk {techName}
    </div>
  );
  return (
    <div className="space-y-1">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">
        {errors.length} Error Terbaru
      </div>
      {errors.map((e, i) => (
        <div key={i} className="border border-red-200 bg-red-50/50 rounded-sm p-2 text-[11px]" data-testid={`error-log-${i}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {e.http_status && (
                <Badge className="bg-red-600 text-white text-[9px] rounded-sm">{e.http_status}</Badge>
              )}
              <span className="font-semibold text-red-800">{e.operation}</span>
              {e.event_summary && (
                <span className="text-zinc-600 italic">· "{e.event_summary}"</span>
              )}
            </div>
            <span className="text-[9px] text-zinc-500 font-mono">{fmtDate(e.timestamp)}</span>
          </div>
          <div className="text-zinc-700 mt-1 font-mono text-[10px] break-all">{e.error}</div>
          {e.event_id && (
            <div className="text-[9px] text-zinc-500 mt-1">event: <code>{e.event_id}</code></div>
          )}
        </div>
      ))}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMin = Math.round((now - d) / 60000);
    if (diffMin < 1) return "baru saja";
    if (diffMin < 60) return `${diffMin} mnt lalu`;
    if (diffMin < 1440) return `${Math.round(diffMin/60)} jam lalu`;
    return d.toLocaleString("id-ID", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" });
  } catch { return iso; }
}
